/**
 * Ruang Pesanan customer — CS / Kepala Cabang / Owner, di halaman Permintaan Desain.
 *
 * U1 audit W1–W4 (12 Okt 2026): tautan Ruang Pesanan hanya ada di pesan WhatsApp (tokennya disimpan sebagai hash).
 * Bila pesan itu gagal lahir atau customer kehilangannya, tombol "Kirim ulang" mengantrekannya lagi dan menampilkannya
 * supaya bisa disalin ke chat customer.
 *
 * K226 (BUSINESS-104 A, 26 Okt 2026): tautannya kini TETAP per customer ("Ruang Pesanan saya", semua pesanan dalam
 * satu tautan). "Kirim ulang" mengirim tautan yang SAMA — tidak ada yang mati. "Ganti tautan" (alasan wajib, tercatat)
 * hanya bila tautan bocor atau HP customer hilang: tautan lama + tautan per pesanan lama berhenti berlaku.
 */
import React, { useState } from "react";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { konfirmasi, mintaTeks } from "../../lib/dialogAplikasi";

export default function KirimUlangRuangPesanan({ drId }) {
  const [hasil, setHasil] = useState(null);
  const [galat, setGalat] = useState("");
  const [sibuk, setSibuk] = useState(false);

  async function kirim() {
    const ya = await konfirmasi(
      "Tautan Ruang Pesanan customer (tetap, sama dengan sebelumnya) dikirim lagi ke WhatsApp-nya. Lanjutkan?");
    if (!ya) return;
    setGalat("");
    setSibuk(true);
    try {
      const r = await api.post(`/ruang-pesanan-staf/${drId}/kirim-ulang`, {});
      setHasil(r.data);
    } catch (e) {
      setGalat(apiErrorMessage(e, "Tautan belum bisa dikirim ulang. Coba lagi sebentar."));
    } finally {
      setSibuk(false);
    }
  }

  async function ganti() {
    const alasan = await mintaTeks(
      "Tautan lama customer ini (dan tautan per pesanan lamanya) akan berhenti berlaku. Tulis alasannya, mis. "
      + "\"HP customer hilang\" atau \"tautan tersebar ke orang lain\".",
      { judul: "Ganti tautan Ruang Pesanan", minPanjang: 3, ya: "Ganti tautan", multiBaris: false });
    if (!alasan) return;
    setGalat("");
    setSibuk(true);
    try {
      const r = await api.post(`/ruang-pesanan-staf/${drId}/ganti-tautan`, { alasan });
      setHasil(r.data);
    } catch (e) {
      setGalat(apiErrorMessage(e, "Tautan belum bisa diganti. Coba lagi sebentar."));
    } finally {
      setSibuk(false);
    }
  }

  async function salin() {
    try { await navigator.clipboard.writeText(hasil.tautan); } catch { /* tetap bisa disalin manual */ }
  }

  return (
    <Card data-testid="rp-kirim-ulang">
      <CardHeader><CardTitle className="text-base">Ruang Pesanan customer</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          Satu tautan untuk semua pesanan customer ini. Customer belum menerima tautannya, atau tautannya hilang?
          Kirim ulang dari sini.
        </p>
        {galat && <p className="text-danger-soft-foreground" role="alert">{galat}</p>}
        {hasil && (
          <div className="space-y-2" data-testid="rp-kirim-ulang-hasil">
            <p>{hasil.pesan}</p>
            <div className="flex gap-2">
              <Input readOnly value={hasil.tautan || ""} className="font-mono text-xs" data-testid="rp-kirim-ulang-tautan" />
              <Button type="button" variant="outline" size="sm" onClick={salin}>Salin</Button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={sibuk} onClick={kirim} data-testid="rp-kirim-ulang-btn">
            {sibuk ? "Mengirim…" : "Kirim ulang tautan Ruang Pesanan"}
          </Button>
          <Button type="button" variant="ghost" disabled={sibuk} onClick={ganti} data-testid="rp-ganti-tautan-btn">
            Ganti tautan
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
